import {
  DropdownDivider,
  DropdownItem,
  openDialog,
  useTranslationContext,
} from 'platform/components';
import {Box, Icon, Show, Text} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useEagGridConfig} from '../../../../context/useEagGridConfig';
import {useEagGridStore} from '../../../../store/useEagGridStore';
import {Preset} from '../../../../types/Api';
import {DataGridPermissionKeysEnum} from '../../../../types/DataGridPermissionKeysEnum';
import {PresetApiStateEnum} from '../../constants/PresetApiStateEnum';
import {useSharePresetToUser} from '../../hooks/useSharePresetToUser';
import {SharePresetForUserDialog} from '../SharePresetForUserDialog/SharePresetForUserDialog';

export type PresetButtonActionsProps = RequiredTestIdProps & {
  preset: Preset;
  presetChanged: boolean;
  activePresetId: string | undefined;
  updatePreset: (presetId: string) => void;
  resetOverrides: () => void;
  duplicatePreset: (presetId: string) => void;
  applyPresetToVirtual: (
    presetId: string,
    saveCurrentPreset?: boolean,
    onSuccess?: () => void
  ) => void;
  onCopySuccess?: () => void;
  applyToDefaultDisabled?: boolean;
  applyToDefaultState?: PresetApiStateEnum;
  deletePreset: (presetId: string) => void;
  openEditModal: () => void;
};

export function PresetButtonActions(props: PresetButtonActionsProps) {
  const t = useTranslationContext();
  const [isConfirmingCopy, setIsConfirmingCopy] = useState(false);
  const store = useEagGridStore();
  const {useGetUserOptions, usePermissions} = useEagGridConfig();

  // usePermissions/useGetUserOptions are host accessors from EagGridConfigProvider (see its JSDoc).
  const permissions = usePermissions({
    permissionKeys: [DataGridPermissionKeysEnum.CUSTOM_SHARE_PRESET],
  });

  // Bind the share mutation here, inside the EagGrid providers. The dialog is portaled
  // outside them by openDialog, so it must receive onSubmit as a prop rather than call
  // useHttpCalls (→ useEagGridStore) itself.
  const {onSubmit: onSharePresetSubmit} = useSharePresetToUser(
    props.preset.id,
    store.getState().gridCode
  );

  const applyToDefaultIcon = match(props.applyToDefaultState)
    .with(PresetApiStateEnum.success, () => <Icon value="action/check_circle" />)
    .with(PresetApiStateEnum.error, () => <Icon value="alert/error" />)
    .otherwise(() => <Icon value="action/input" />);

  const onPresetShare = () => {
    if (isNil(useGetUserOptions)) {
      throw new Error('useGetUserOptions - is not defined in EagGrid provider');
    }

    openDialog(
      <SharePresetForUserDialog
        onSubmit={onSharePresetSubmit}
        useGetUserOptionsQuery={useGetUserOptions}
        data-testid={suffixTestId('sharePresetToUser', props)}
      />,
      {
        title: t('page.datagrid.labels.sharePresetDialogTitle'),
        scrollBehavior: 'outside',
        size: 'small',
        'data-testid': suffixTestId('sharePresetToUser', props),
      }
    );
  };

  const onCopyToDefault = () => {
    if (props.presetChanged && isNotNil(props.activePresetId)) {
      setIsConfirmingCopy(true);
      return;
    }
    if (props.onCopySuccess) {
      props.applyPresetToVirtual(props.preset.id, false, props.onCopySuccess);
    } else {
      props.applyPresetToVirtual(props.preset.id);
    }
  };

  const confirmCopy = (saveCurrentPreset: boolean) => {
    setIsConfirmingCopy(false);
    props.applyPresetToVirtual(props.preset.id, saveCurrentPreset, props.onCopySuccess);
  };

  if (isConfirmingCopy) {
    return (
      <Box padding={2} width={72}>
        <Box paddingBottom={2}>
          <Text size="small" alternative>
            {t('general.notifications.unsavedChanges.title')}
          </Text>
          <Text size="small">{t('page.datagrid.copyToDefault.unsavedChanges')}</Text>
        </Box>
        <DropdownItem
          label={t('page.datagrid.copyToDefault.saveAndCopy')}
          onClick={() => confirmCopy(true)}
          closeOnClick={false}
          data-testid={suffixTestId('saveAndCopyAction', props)}
        />
        <DropdownItem
          label={t('page.datagrid.copyToDefault.discardAndCopy')}
          onClick={() => confirmCopy(false)}
          closeOnClick={false}
          data-testid={suffixTestId('discardAndCopyAction', props)}
        />
        <DropdownItem
          label={t('general.actions.cancel')}
          onClick={() => setIsConfirmingCopy(false)}
          closeOnClick={false}
          data-testid={suffixTestId('cancelCopyAction', props)}
        />
      </Box>
    );
  }

  return (
    <>
      <DropdownItem
        key="update"
        isDisabled={
          props.applyToDefaultDisabled ||
          !(props.presetChanged && props.preset.id === props.activePresetId)
        }
        onClick={() => props.updatePreset(props.preset.id)}
        prefix={<Icon value="action/update" />}
        label={t('general.actions.update')}
        data-testid={suffixTestId('updateAction', props)}
      />
      <DropdownItem
        key="reset"
        isDisabled={
          props.applyToDefaultDisabled ||
          !(props.presetChanged && props.preset.id === props.activePresetId)
        }
        onClick={() => props.resetOverrides()}
        prefix={<Icon value="action/restore" />}
        label={t('general.actions.resetOverrides')}
        data-testid={suffixTestId('resetAction', props)}
      />
      <DropdownItem
        key="edit"
        isDisabled={props.applyToDefaultDisabled}
        onClick={props.openEditModal}
        prefix={<Icon value="image/edit" />}
        label={t('general.actions.edit')}
        data-testid={suffixTestId('editAction', props)}
      />
      <DropdownItem
        key="copy"
        isDisabled={props.applyToDefaultDisabled}
        onClick={() => props.duplicatePreset(props.preset.id)}
        prefix={<Icon value="content/copy" />}
        label={t('general.actions.duplicate')}
        data-testid={suffixTestId('duplicateAction', props)}
      />
      <DropdownItem
        key="applyToDefault"
        isDisabled={props.applyToDefaultDisabled}
        isLoading={props.applyToDefaultState === PresetApiStateEnum.loading}
        closeOnClick={false}
        onClick={onCopyToDefault}
        prefix={applyToDefaultIcon}
        label={t('page.datagrid.actions.copyToDefault')}
        data-testid={suffixTestId('applyToDefaultAction', props)}
      />
      <Show when={isNotNil(useGetUserOptions) && permissions?.[0]}>
        <DropdownItem
          key="share"
          isDisabled={props.applyToDefaultDisabled}
          onClick={onPresetShare}
          prefix={<Icon value="social/share" />}
          label={t('general.actions.share')}
          data-testid={suffixTestId('shareAction', props)}
        />
      </Show>
      <DropdownDivider />
      <DropdownItem
        key="delete"
        isDisabled={props.applyToDefaultDisabled}
        prefix={<Icon value="action/delete" />}
        label={t('general.actions.delete')}
        onClick={() => props.deletePreset(props.preset.id)}
        data-testid={suffixTestId('deleteAction', props)}
      />
    </>
  );
}
